import React, {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import styles from "./SelectFromQuestionBankModal.module.css";
import QuestionContent from "../../../components/common/QuestionContent/QuestionContent";
import {getQuestions} from "../../../services/questionsApi";
import Loader from "../../../components/common/Loader/Loader";
import {assignQuestionsToExamBase, getExamBaseById} from "../../../services/examBasesApi";

const SelectFromQuestionBankModal = ({isOpen, onClose, examBaseId, onSuccess}) => {
    const { t } = useTranslation();
    const [questionTextFilter, setQuestionTextFilter] = useState("");

    // The template's topics, so a multi-topic exam can be browsed one topic at a time instead of as
    // one interleaved list. null = "all topics".
    const [topics, setTopics] = useState([]);
    const [activeTopicId, setActiveTopicId] = useState(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [questions, setQuestions] = useState([]);
    const [error, setError] = useState("");

    const [allSelectedAcrossPages, setAllSelectedAcrossPages] = useState(false);
    const [selectedIds, setSelectedIds] = useState({});
    const [excludedIds, setExcludedIds] = useState({});

    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const getQuestionKey = (q, i) => q.id || q.uuid || String(i);

    useEffect(() => {
        if (!isOpen) return;

        setPageNo(1);
        setQuestionTextFilter("");
        setQuestions([]);
        setError("");
        setSaving(false);
        setActiveTopicId(null);

        setAllSelectedAcrossPages(false);
        setSelectedIds({});
        setExcludedIds({});
    }, [isOpen]);

    // The template's topic set drives the filter chips. Non-fatal: on failure the picker just falls
    // back to the single "all topics" list it showed before.
    useEffect(() => {
        if (!isOpen || !examBaseId) return;
        let cancelled = false;
        getExamBaseById(examBaseId)
            .then((dto) => {
                if (cancelled) return;
                setTopics(Array.isArray(dto?.subjects) ? dto.subjects : []);
            })
            .catch(() => {
                if (!cancelled) setTopics([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, examBaseId]);

    useEffect(() => {
        if (!isOpen) return;
        fetchQuestionsFromBank();
    }, [isOpen, pageNo, activeTopicId]);

    const fetchQuestionsFromBank = async (targetPageNo = pageNo, textFilter = questionTextFilter) => {
        if (!isOpen) return;

        try {
            setLoading(true);
            setError("");

            const filters = {
                pageNo: targetPageNo,
                pageSize,
                examBaseId,
                assignedOnly: false,
            };

            // The backend intersects this with the template's own topics, so it can only narrow.
            if (activeTopicId != null) {
                filters.subjectId = activeTopicId;
            }

            if (textFilter.trim()) {
                filters.questionText = textFilter.trim();
            }

            const raw = await getQuestions(filters);
            const data = Array.isArray(raw) ? raw : raw?.content || [];

            setQuestions(data);

            const total =
                typeof raw?.totalPages === "number" && raw.totalPages > 0
                    ? raw.totalPages
                    : Math.ceil(data.length / pageSize) || 1;

            setTotalPages(total);
            setTotalElements(raw?.totalElements || 0);
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                t("pages.selectFromQuestionBank.loadError");

            setQuestions([]);
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const isChecked = (q, i) => {
        const key = getQuestionKey(q, i);

        if (allSelectedAcrossPages) {
            return !excludedIds[key];
        }

        return !!selectedIds[key];
    };

    const toggleOne = (q, i) => {
        const key = getQuestionKey(q, i);

        if (allSelectedAcrossPages) {
            setExcludedIds((prev) => ({
                ...prev,
                [key]: !prev[key],
            }));
            return;
        }

        setSelectedIds((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const currentPageSelectedCount = useMemo(() => {
        return questions.reduce((acc, q, i) => acc + (isChecked(q, i) ? 1 : 0), 0);
    }, [questions, allSelectedAcrossPages, selectedIds, excludedIds]);

    const allSelectedOnPage = useMemo(() => {
        return questions.length > 0 && currentPageSelectedCount === questions.length;
    }, [questions.length, currentPageSelectedCount]);

    const indeterminate = useMemo(() => {
        return currentPageSelectedCount > 0 && currentPageSelectedCount < questions.length;
    }, [currentPageSelectedCount, questions.length]);

    const selectedCount = useMemo(() => {
        if (allSelectedAcrossPages) {
            const excludedCount = Object.keys(excludedIds).filter((key) => excludedIds[key]).length;
            return Math.max(totalElements - excludedCount, 0);
        }

        return Object.keys(selectedIds).filter((key) => selectedIds[key]).length;
    }, [allSelectedAcrossPages, excludedIds, selectedIds, totalElements]);

    // Questions of the current page, split into one block per topic. The backend orders a multi-topic
    // browse by topic, so a block is normally a whole topic; a topic straddling a page boundary just
    // gets its header repeated on the next page. `index` is the question's position on the page —
    // selection keys are derived from it, so it must survive the regrouping.
    const topicBlocks = useMemo(() => {
        const blocks = [];
        const indexByKey = new Map();
        questions.forEach((q, index) => {
            const key = q.subjectId != null ? String(q.subjectId) : "__none__";
            if (!indexByKey.has(key)) {
                indexByKey.set(key, blocks.length);
                blocks.push({
                    key,
                    label: [q.parentName, q.topicTitle].filter(Boolean).join(" › "),
                    entries: [],
                });
            }
            blocks[indexByKey.get(key)].entries.push({q, index});
        });
        return blocks;
    }, [questions]);

    // Headers earn their space only in the "all topics" view, and only when the page actually spans
    // more than one topic — a filtered view already says which topic you are looking at.
    const showTopicHeaders = activeTopicId == null && topicBlocks.length > 1;

    if (!isOpen) return null;

    const selectTopic = (topicId) => {
        if (topicId === activeTopicId) return;
        // Selection is expressed relative to the active filter ("all N matching"), so it cannot carry
        // across a filter change — same reset the text search does.
        setActiveTopicId(topicId);
        setPageNo(1);
        setAllSelectedAcrossPages(false);
        setSelectedIds({});
        setExcludedIds({});
    };

    const toggleAll = () => {
        if (allSelectedAcrossPages) {
            setAllSelectedAcrossPages(false);
            setSelectedIds({});
            setExcludedIds({});
        } else {
            setAllSelectedAcrossPages(true);
            setSelectedIds({});
            setExcludedIds({});
        }
    };

    const goToPrevPage = () => {
        setPageNo((prev) => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        if (pageNo < totalPages) {
            setPageNo((prev) => prev + 1);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setPageNo(1);
        setAllSelectedAcrossPages(false);
        setSelectedIds({});
        setExcludedIds({});
        await fetchQuestionsFromBank(1, questionTextFilter);
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // Echo the topic filter back: with allSelected the backend resolves the id set itself, so
            // without it "select all" inside one topic would pull in every topic of the template.
            const subjectIds = activeTopicId != null ? [activeTopicId] : null;

            const payload = allSelectedAcrossPages
                ? {
                    examBaseId,
                    allSelected: true,
                    excludedQuestionIds: Object.keys(excludedIds).filter((key) => excludedIds[key]),
                    questionText: questionTextFilter.trim() || null,
                    subjectIds,
                }
                : {
                    examBaseId,
                    allSelected: false,
                    questionIds: Object.keys(selectedIds).filter((key) => selectedIds[key]),
                    subjectIds,
                };

            await assignQuestionsToExamBase(examBaseId, payload);

            onSuccess?.(payload);
            onClose?.();
            setPageNo(1);
            setQuestionTextFilter("");
            setQuestions([]);
            setError("");
            setSaving(false);

            setAllSelectedAcrossPages(false);
            setSelectedIds({});
            setExcludedIds({});
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                t("pages.selectFromQuestionBank.assignError");
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <header className={styles.header}>
                    <div className={styles.titleWrap}>
                        <h2 className={styles.title}>{t("pages.selectFromQuestionBank.title")}</h2>
                    </div>
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={onClose}
                        aria-label={t("pages.selectFromQuestionBank.closeAria")}
                    >
                        <span className={styles.closeIcon} aria-hidden>×</span>
                    </button>
                </header>

                <div className={styles.body}>
                    {error && (
                        <div className={styles.error} role="alert">
                            <span className={styles.errorIcon}>!</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <form className={styles.filterSection} onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder={t("pages.selectFromQuestionBank.questionTextPlaceholder")}
                            className={styles.filterInput}
                            value={questionTextFilter}
                            onChange={(e) => setQuestionTextFilter(e.target.value)}
                        />

                        <button type="submit" className={styles.primaryBtn}>
                            {t("common.search")}
                        </button>
                    </form>

                    {topics.length > 1 && (
                        <div className={styles.topicFilter} role="group" aria-label={t("pages.selectFromQuestionBank.filterByTopic")}>
                            <button
                                type="button"
                                className={`${styles.topicChip} ${activeTopicId == null ? styles.topicChipActive : ""}`}
                                onClick={() => selectTopic(null)}
                            >
                                {t("pages.selectFromQuestionBank.allTopics")}
                            </button>
                            {topics.map((tp) => (
                                <button
                                    key={tp.id}
                                    type="button"
                                    className={`${styles.topicChip} ${activeTopicId === tp.id ? styles.topicChipActive : ""}`}
                                    onClick={() => selectTopic(tp.id)}
                                    title={tp.parentName ? `${tp.parentName} › ${tp.title}` : tp.title}
                                >
                                    {tp.title}
                                </button>
                            ))}
                        </div>
                    )}

                    <section className={styles.section} aria-labelledby="ai-questions-heading">
                        <div className={styles.sectionHeader}>
                            <h3 id="ai-questions-heading" className={styles.sectionTitle}>
                                {t("pages.selectFromQuestionBank.questions")}
                            </h3>

                            <div className={styles.pagination}>
                                <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
                                    {t("common.previous")}
                                </button>
                                <span>
                                    {t("common.pageOf", { page: pageNo, total: totalPages || 1 })}
                                </span>
                                <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
                                    {t("common.next2")}
                                </button>
                            </div>
                        </div>

                        <div className={styles.preview}>
                            {!loading && questions.length === 0 && (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIconWrap}>
                                        <span className={styles.emptyIcon}>📝</span>
                                    </div>
                                    <p className={styles.emptyText}>
                                        {t("pages.selectFromQuestionBank.noQuestionsFound")}
                                    </p>
                                </div>
                            )}

                            {loading && <Loader />}

                            {!loading && questions.length > 0 && (
                                <>
                                    <div className={styles.previewToolbar}>
                                        <label className={styles.selectAll}>
                                            <input
                                                type="checkbox"
                                                checked={allSelectedOnPage}
                                                ref={(el) => {
                                                    if (el) el.indeterminate = indeterminate;
                                                }}
                                                onChange={toggleAll}
                                            />
                                            <span>{t("pages.selectFromQuestionBank.selectAll")}</span>
                                        </label>

                                        <span className={styles.selectedBadge}>
                                            {selectedCount} / {totalElements}
                                        </span>
                                    </div>

                                    {topicBlocks.map((block) => (
                                        <div key={block.key} className={styles.topicBlock}>
                                            {showTopicHeaders && (
                                                <div className={styles.topicBlockHeader}>
                                                    <span>{block.label || t("pages.selectFromQuestionBank.otherTopic")}</span>
                                                    <span className={styles.topicBlockCount}>{block.entries.length}</span>
                                                </div>
                                            )}

                                            <ul className={styles.questionList}>
                                                {block.entries.map(({q, index: i}) => {
                                                    const key = getQuestionKey(q, i);

                                                    return (
                                                        <li key={key} className={styles.questionCard}>
                                                            <label className={styles.questionCardLabel}>
                                                                <input
                                                                    type="checkbox"
                                                                    className={styles.questionCheckbox}
                                                                    checked={isChecked(q, i)}
                                                                    onChange={() => toggleOne(q, i)}
                                                                />

                                                                <span className={styles.questionNumber}>
                                                                    {(pageNo - 1) * pageSize + i + 1}
                                                                </span>

                                                                <div className={styles.questionText}>
                                                                    <QuestionContent text={q.text} />
                                                                    {/* The block header already names the topic — only label the
                                                                        question itself when there is no header above it. */}
                                                                    {!showTopicHeaders && (() => {
                                                                        const path = [q.parentName, q.topicTitle].filter(Boolean).join(" › ");
                                                                        return path ? <span className={styles.questionSubjectPath}>{path}</span> : null;
                                                                    })()}
                                                                </div>
                                                            </label>

                                                            <ul className={styles.optionsList}>
                                                                {q.options?.map((o, idx) => (
                                                                    <li
                                                                        key={o.optionText + idx}
                                                                        className={`${styles.optionItem} ${o.correct ? styles.optionCorrect : ""}`}
                                                                    >
                                                                        <span className={styles.optionLetter}>
                                                                            {String.fromCharCode(65 + idx)}
                                                                        </span>

                                                                        <div className={styles.optionText}>
                                                                            <QuestionContent text={o.optionText} />
                                                                            {o.attachmentUrl && (
                                                                                <img
                                                                                    src={o.attachmentUrl}
                                                                                    alt="option"
                                                                                    className={styles.imagePreview}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ))}

                                    <div className={styles.footerBar}>
                                        <button
                                            type="button"
                                            className={styles.successBtn}
                                            onClick={handleSave}
                                            disabled={saving || loading || selectedCount === 0}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className={styles.btnSpinner} aria-hidden />
                                                    <span>{t("pages.selectFromQuestionBank.adding")}</span>
                                                </>
                                            ) : (
                                                t("pages.selectFromQuestionBank.addToExam", { count: selectedCount })
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default SelectFromQuestionBankModal;